import type { PaquetesResponse } from "@/pages/admin/Paquetes";
import { axiosInstance } from "@retro-ia/shared/axios";
import type {
  CreatePaqueteInput,
  UpdatePaqueteInput,
} from "@/lib/validaciones/paquete.validacion";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { toast } from "sonner";

export const useRegisterPaquete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePaqueteInput) => {
      const res = await axiosInstance.post("/paquetes", data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["paquetes"] });
      queryClient.invalidateQueries({ queryKey: ["clientesFinalesProgramas"] });
      queryClient.invalidateQueries({ queryKey: ["programas-dashboard-admin"] });
      toast.success("Paquete creado exitosamente");
    },
    onError: () => {
      toast.error("Error al crear el paquete");
    },
  });
};

export const useUpdatePaquete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdatePaqueteInput) => {
      const res = await axiosInstance.patch(`/paquetes/${data.id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["paquetes"] });
      queryClient.invalidateQueries({ queryKey: ["clientesFinalesProgramas"] });
      queryClient.invalidateQueries({ queryKey: ["programas-dashboard-admin"] });
      toast.success("Paquete actualizado exitosamente");
    },
    onError: () => {
      toast.error("Error al actualizar el paquete");
    },
  });
};

export const useDeleteLogicoPaquete = (options?: { queryKey?: QueryKey }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await axiosInstance.put(`/paquetes/${id}`, undefined, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("El paquete fue eliminado.");
      queryClient.invalidateQueries({
        queryKey: options?.queryKey ?? ["paquetes"],
      });
      queryClient.invalidateQueries({ queryKey: ["clientesFinalesProgramas"] });
      queryClient.invalidateQueries({ queryKey: ["programas-dashboard-admin"] });
    },
    onError: () => {
      toast.error("Error al eliminar el paquete.");
    },
  });
};

export const usePaquetes = (page: number, limit: number, search: string) => {
  return useQuery<PaquetesResponse, Error>({
    queryKey: ["paquetes", page, limit, search],
    queryFn: async () => {
      const response = await axiosInstance.get("/paquetes", {
        params: { page, limit, search },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
};
